import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { GET_FOOTER_BLOCK } from './queries/get-footer-block.query';
import { GET_PAGE_ASSEMBLY_NESTED } from './queries/get-page-assembly-nested.query';
import { GET_ALL_SLUGS } from './queries/get-all-page-slugs.query';
import { GET_HOME_PAGE_QUERY } from './queries/get-home-page.query';
import { GET_HEADER_BLOCK } from './queries/get-header-block.query';
import { GET_CONTAINER_BLOCKS } from './queries/get-container-blocks.query';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';
import { FooterApiResponse } from '@app/shared/types/footer.types';
import { BrowserCheckService } from '@app/utils/browser-check.service';
import { AllSlugsApiResponse } from '@app/shared/types/all-slugs.types';

@Injectable({
  providedIn: 'root',
})
export class GraphqlService {
  constructor(
    private apollo: Apollo,
    private browserCheck: BrowserCheckService,
  ) {}

  getFooterBlockData(
    id: string,
  ): Observable<ApolloQueryResult<FooterApiResponse>> {
    return this.apollo.query<FooterApiResponse>({
      query: GET_FOOTER_BLOCK,
      variables: { id },
    });
  }

  getPageAssemblyNestedTask(slug: string) {
    return this.apollo.query({
      query: GET_PAGE_ASSEMBLY_NESTED,
      variables: { slug },
    });
  }

  getAllPageAssemblySlugs(): Observable<AllSlugsApiResponse> {
    return this.apollo.query({
      query: GET_ALL_SLUGS,
    });
  }

  getHeaderBlock() {
    return this.apollo.query({
      query: GET_HEADER_BLOCK,
    });
  }

  getHomeContent(): Observable<any> {
    return this.apollo
      .query({
        query: GET_HOME_PAGE_QUERY,
      })
      .pipe(map((result: any) => result));
  }

  getContainerBlocks(ids: string[]) {
    return this.apollo.query({
      query: GET_CONTAINER_BLOCKS,
      variables: { ids },
    });
  }

  getPageProtectionStatus(slug: string): Observable<boolean> {
    if (this.browserCheck.isBrowser()) {
      const cachedData = JSON.parse(
        sessionStorage.getItem('pageAssemblies') || '[]',
      );
      if (cachedData.length > 0) {
        const page = cachedData.find((item: any) => item.slug === slug);
        return of(page ? page.isProtected : false);
      } else {
        return this.getAllPageAssemblySlugs().pipe(
          map((result) => {
            const items = result.data?.pageAssemblyCollection?.items || [];
            sessionStorage.setItem('pageAssemblies', JSON.stringify(items));
            const page = items.find((item: any) => item.slug === slug);
            return page?.isProtected ?? false;
          }),
          catchError(() => of(false)), // In case of an error, assume page is public
        );
      }
    }

    return of(false);
  }
}
